import '../css/app.css';

import { createInertiaApp } from '@inertiajs/react';
import * as Sentry from '@sentry/react';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { User } from './features/users/types';
import { initializeTheme } from './hooks/use-appearance';

createInertiaApp({
    title: (title) => `${title} - ONYX Case Management System`,
    resolve: (name) => resolvePageComponent(`./pages/${name}.tsx`, import.meta.glob('./pages/**/*.tsx')),
    setup({ el, App, props }) {
        if (import.meta.env.VITE_SENTRY_DSN) {
            Sentry.init({
                dsn: import.meta.env.VITE_SENTRY_DSN,
                release: import.meta.env.VITE_SENTRY_RELEASE,
                environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
                // Adds request headers and IP for users, for more info visit:
                // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii
                sendDefaultPii: true,
                tracesSampler: ({ parentSampled }) => {
                    if (typeof parentSampled === 'boolean') {
                        return parentSampled;
                    }

                    return import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE;
                },
                profilesSampleRate: import.meta.env.VITE_SENTRY_PROFILES_SAMPLE_RATE,
                integrations: [
                    Sentry.browserTracingIntegration(),
                    Sentry.feedbackIntegration({
                        autoInject: false,
                        triggerLabel: 'Feedback',
                        formTitle: 'Feedback',
                        submitButtonLabel: 'Send Feedback',
                        messagePlaceholder: 'Request a feature or report an issue.',
                        successMessageText: 'Thank you for your feedback!',
                        showBranding: false,
                        trackComponents: true,
                        logErrors: true,
                    }),
                ],
            });

            const p = props as unknown as { auth: { user: User } };
            if (p.auth?.user) {
                Sentry.setUser({
                    id: p.auth.user.id,
                    email: p.auth.user.email,
                    username: p.auth.user.username,
                });
            }
        }

        if (import.meta.env.VITE_POSTHOG_TOKEN) {
            posthog.init(import.meta.env.VITE_POSTHOG_TOKEN, {
                api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
                person_profiles: 'identified_only', // or 'always' to create profiles for anonymous users as well
                mask_all_element_attributes: false,
                session_recording: {
                    maskAllInputs: false,
                    maskInputOptions: {
                        password: true,
                    },
                },
            });

            const p = props as unknown as { auth: { user: User } };
            if (p.auth?.user) {
                const { first_name, last_name, username } = p.auth.user;
                posthog.identify(username, {
                    username,
                    name: `${first_name} ${last_name}`,
                    company: p.auth.user.company,
                    role: p.auth.user.role,
                });
            }
        }

        const root = (
            <PostHogProvider client={posthog}>
                <App {...props} />
            </PostHogProvider>
        );

        const renderOptions = {
            // Callback called when an error is thrown and not caught by an ErrorBoundary.
            onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
                console.warn('Uncaught error', error, errorInfo.componentStack);
            }),
            // Callback called when React catches an error in an ErrorBoundary.
            onCaughtError: Sentry.reactErrorHandler(),
            // Callback called when React automatically recovers from errors.
            onRecoverableError: Sentry.reactErrorHandler(),
        };

        if (import.meta.env.SSR) {
            hydrateRoot(el, root, renderOptions);
            return;
        }

        createRoot(el, renderOptions).render(root);
    },
    progress: {
        color: '#ec1b24',
    },
});

// This will set light / dark mode on load...
initializeTheme();
